/* eslint-disable no-shadow */
import { call, takeEvery } from "redux-saga/effects";

import { activateWorkflowModel, loadWorkflowInstances, loadWorkflowModel, updateWorkflowModel } from "./documentWorkflowEditor.api";
import noop from "lodash.noop";

import { getId, injectApiWithContextFactory, injectBoundActionCreatorsFactory, takeFirstAndLatest } from "Utils/sagaUtils";

import { injectBoundGQLClient } from "Utils/injectBoundGQLClient";

import {
    DOCUMENT_ACTIVATE_MODEL_REQUESTED,
    DOCUMENT_LOAD_MODEL_REQUESTED,
    DOCUMENT_SAVE_MODEL_REQUESTED,
    failDocumentActivateWorkflow,
    failDocumentLoadWorkflowModel,
    failDocumentSaveWorkflowModel,
    successDocumentActivateWorkflow,
    successDocumentLoadWorkflowModel,
    successDocumentSaveWorkflowModel,
} from "./documentWorkflowEditor.duck";

export const withBoundActions = injectBoundActionCreatorsFactory(
    {
        successDocumentLoadWorkflowModel,
        failDocumentLoadWorkflowModel,
        successDocumentSaveWorkflowModel,
        failDocumentSaveWorkflowModel,
        successDocumentActivateWorkflow,
        failDocumentActivateWorkflow,
    },
    getId
);

export const withApiContext = injectApiWithContextFactory(
    {
        updateWorkflowModel,
        loadWorkflowModel,
        loadWorkflowInstances,
        activateWorkflowModel,
    },
    injectBoundGQLClient
);

export function* loadDocumentWorkflowModelSaga({
    api: { loadWorkflowModel = noop },
    actions: { successDocumentLoadWorkflowModel = noop, failDocumentLoadWorkflowModel = noop },
    resolve = noop,
    reject = noop,
}) {
    try {
        const result = yield loadWorkflowModel();
        yield successDocumentLoadWorkflowModel(result);
        yield call(resolve);
    } catch (e) {
        console.error(e);
        yield failDocumentLoadWorkflowModel(e);
        yield call(reject, e);
    }
}

const makeApprovalAutoprogress = ({ stage, actionIdentifier }) => ({
    nextStage: stage,
    conditions: [
        {
            assertation: {
                ActionAssertationInput: {
                    type: "APPROVE",
                    status: "VOTED",
                    actionIdentifier,
                    // default count should set to 0 to align value when saving WorkflowSettings. See MD-4656 for more details
                    customstatus: null,
                    count: 0,
                },
            },
        },
    ],
});

const makeAutoprogress = ({ stage, action, kind = "EDIT", buttonText }) => ({
    nextStage: stage,
    conditions: [
        {
            assertation: {
                ActionAssertationInput: {
                    type: kind,
                    status: action,
                    customstatus: `FULFILLED-${buttonText}`,
                    count: 1,
                },
            },
        },
    ],
});

const removeTypenameKeys = (obj, keys = ["__typename"]) =>
    obj !== Object(obj)
        ? obj
        : Array.isArray(obj)
        ? obj.map(item => removeTypenameKeys(item, keys))
        : Object.keys(obj)
              .filter(k => !keys.includes(k))
              .reduce((acc, x) => Object.assign(acc, { [x]: removeTypenameKeys(obj[x], keys) }), {});

export function* saveDocumentWorkflowModelSaga({
    data: {
        formData,
        model,
        deletedStageMap = {},
        // values are: DOCUMENT and HEADING
        workflowFor,
    },
    api: { updateWorkflowModel = noop, loadWorkflowInstances = noop },
    actions: { successDocumentSaveWorkflowModel = noop, failDocumentSaveWorkflowModel = noop },
    resolve = noop,
    reject = noop,
}) {
    try {
        const updatedWorkflowModelStages = formData.nestedList.map(
            ({ primaryAction, otherActions: rawOtherActions, name, id, approvalStage, nextStage }) => {
                const otherActions =
                    rawOtherActions
                        ?.filter(action => action.stage)
                        .map(act => ({
                            ...act,
                            actionIdentifier: undefined,
                            kind: approvalStage ? "APPROVE" : "EDIT",
                        })) || [];
                const modelStage = model?.stages?.find(({ _id }) => _id === id) || {};
                const customstati = [
                    [`${primaryAction.buttonText}`, "FULFILLED"],
                    ...(otherActions || []).map(({ buttonText, action }) => [
                        `${buttonText}`,
                        ["FULFILLED", "REJECTED", "CANCELLED"].includes(action) ? action : "FULFILLED",
                    ]),
                ]
                    .filter(([i]) => !!i)
                    .map(([i, j]) => `${j}-${i}`);

                const createActions = approvalStage
                    ? [
                          {
                              kind: "APPROVE",
                              name: `Approval action on stage ${name} (${id})`,
                              customstati,
                              subject: [],
                          },
                      ]
                    : [
                          {
                              kind: "EDIT",
                              name: `Edit action on stage ${name} (${id})`,
                              customstati,
                              subject: [],
                          },
                      ];

                const autoprogress = approvalStage
                    ? [
                          makeApprovalAutoprogress({
                              ...primaryAction,
                              stage: nextStage,
                          }),
                      ].filter(i => !!i)
                    : [
                          makeAutoprogress({
                              ...primaryAction,
                              kind: "EDIT",
                          }),
                          ...(otherActions || []).map(makeAutoprogress),
                      ].filter(i => !!i);

                const primaryActionNextStage = approvalStage ? nextStage : primaryAction.stage;
                const nextStages = [
                    { _id: primaryActionNextStage, name: primaryAction.buttonText },
                    ...(otherActions || []).map(({ stage, buttonText }) => ({
                        _id: approvalStage ? nextStage : stage,
                        name: buttonText,
                    })),
                ].filter(({ _id }) => _id);

                const updatedStage = {
                    _id: id,
                    name: name || modelStage.name,
                    description: modelStage.description,
                    dueDate: modelStage.dueDate,
                    defaultActionType: modelStage.defaultActionType || "APPROVE",
                    autoprogress: autoprogress || modelStage.autoprogress || [],
                    entryguards: modelStage.entryguards || [],
                    exitguards: [] || modelStage.exitguards || [],
                    nextStages,
                    prevStages: modelStage.prevStages || [],
                    onEnter: {
                        explicit_deny: modelStage.explicit_deny || [],
                        createActions,
                    },
                };
                return updatedStage;
            }
        );
        const updatedModel = { ...model, stages: updatedWorkflowModelStages, defaultDisabled: false };
        const updatedModelNoTypeName = removeTypenameKeys(updatedModel);

        const workflowInstancesToUpdate = yield loadWorkflowInstances({ modelId: model?._id });
        const workflowInstanceIds = workflowInstancesToUpdate.edges
            .filter(({ node }) => node.workflowmodelid === model?._id)
            .map(({ node }) => node?._id);

        const resolveStageMap = (stage, map) => {
            const mappedStage = map[stage];
            if (mappedStage && map[mappedStage]) return resolveStageMap(mappedStage, map);
            if (mappedStage) return mappedStage;
            return stage;
        };
        const stagemap = Object.fromEntries(
            Object.entries(deletedStageMap).map(([deleted, newStage]) => [deleted, resolveStageMap(newStage, deletedStageMap)])
        );
        const result = yield updateWorkflowModel({
            model: updatedModelNoTypeName,
            workflowInstances: workflowInstanceIds,
            deletedStageMap: stagemap,
            workflowFor,
        });
        yield successDocumentSaveWorkflowModel(result);
        yield call(resolve);
    } catch (e) {
        console.error(e);
        yield failDocumentSaveWorkflowModel(e);
        yield call(reject, e);
    }
}

export function* activateWorkflowModelSaga({
    data,
    api: { activateWorkflowModel = noop },
    actions: { successActivateWorkflow = noop, failActivateWorkflow = noop },
    resolve = noop,
    reject = noop,
}) {
    try {
        const stagesForInput = data.stages.map(stage => {
            return {
                _id: stage._id,
                name: name || stage.name,
                description: stage.description,
                dueDate: stage.dueDate,
                defaultActionType: stage.defaultActionType || "APPROVE",
                entryguards: stage.entryguards || [],
                exitguards: [] || stage.exitguards || [],
                prevStages: stage.prevStages || [],
                onEnter: stage.onEnter,
                nextStages: stage.nextStageTransitions.map(nxt => ({
                    _id: nxt.to,
                    name: nxt.name,
                })),
                prevStages: stage.prevStageTransitions || [],
                autoprogress: stage.autoprogress.map(auto => ({
                    nextStage: auto.nextStageId,
                    conditions: auto.conditions.map(cond => ({
                        ...cond,
                        assertation: {
                            ActionAssertationInput: {
                                ...cond.assertation,
                                customstatus: cond.customStatus,
                                customStatus: undefined,
                            },
                        },
                    })),
                })),
            };
        });
        const updatedModel = { ...data, stages: stagesForInput, defaultDisabled: false };
        const updatedModelNoTypeName = removeTypenameKeys(updatedModel);

        const result = yield activateWorkflowModel({
            model: updatedModelNoTypeName,
        });
        yield successActivateWorkflow(result);
        yield call(resolve);
    } catch (e) {
        console.error(e);
        yield failActivateWorkflow(e);
        yield call(reject, e);
    }
}

export default () =>
    function* rootSaga() {
        yield takeFirstAndLatest(DOCUMENT_LOAD_MODEL_REQUESTED, withApiContext(withBoundActions(loadDocumentWorkflowModelSaga)));
        yield takeEvery(DOCUMENT_SAVE_MODEL_REQUESTED, withApiContext(withBoundActions(saveDocumentWorkflowModelSaga)));
        yield takeEvery(DOCUMENT_ACTIVATE_MODEL_REQUESTED, withApiContext(withBoundActions(activateWorkflowModelSaga)));
    };
